import { NotificationProps, useNotifications } from "@mantine/notifications";
import React, { useCallback } from "react";
import * as iconModule from "react-icons/md";
import { SupportedFeatures } from "../../features/payslips/services/model/FeatureConfiguration";
import { MessageType, MessageTypeTrigger } from "./types";
import { useMessageTemplates } from "./StrapiCMSApiService";
import { replacePlaceHolders } from "./utils";

interface ToastProps extends Omit<NotificationProps, "message"> {
  icon?: string;
}

interface ToastsTriggerProps {
  toast?: ToastProps;
  resourceType?: SupportedFeatures | "General";
  trigger?: MessageTypeTrigger;
  data?: any;
  strapiId?: string;
}

const getDynamicIcon = (MdIconName: string): React.ReactNode | null => {
  try {
    const Icon = (iconModule as { [key: string]: any })[MdIconName];
    return Icon ? React.createElement(Icon, { size: 24 }) : null;
  } catch (error) {
    return null;
  }
};

const getToastTemplate = (
  messageTemplates: { data: MessageType[] } | undefined,
  { strapiId, resourceType, trigger }: ToastsTriggerProps
): MessageType | undefined => {
  if (!messageTemplates?.data) return undefined;

  if (strapiId) {
    return messageTemplates.data.find(
      (t) =>
        t.attributes.message_type === "toast" &&
        t.attributes.template_id === strapiId
    );
  }

  if (resourceType && trigger) {
    return messageTemplates.data.find(
      (t) =>
        t.attributes.resource_type === resourceType &&
        t.attributes.trigger === trigger
    );
  }

  return undefined;
};

const TOAST_COLORS = {
  DEFAULT: "#ED7687",
  INFO: "#062133",
  SUCCESS: "#107A54",
  WARNING: "#FF922B",
  ERROR: "#F03E3E",
} as const;

const getToastColor = (
  template: MessageType,
  defaultColor?: string
): string => {
  if (!template.attributes.Type) return defaultColor ?? TOAST_COLORS.DEFAULT;

  const colorMap: Record<string, string> = {
    Default: TOAST_COLORS.INFO,
    Info: TOAST_COLORS.INFO,
    Success: TOAST_COLORS.SUCCESS,
    Warning: TOAST_COLORS.WARNING,
    Error: TOAST_COLORS.ERROR,
  };

  return (
    colorMap[template.attributes.Type] ?? defaultColor ?? TOAST_COLORS.DEFAULT
  );
};

const createToastMessage = (
  template: MessageType,
  data: any,
  toastColor: string
): string => {
  let message = data
    ? replacePlaceHolders(template.attributes.message_content, data)
    : template.attributes.message_content;

  if (template.attributes.action_text) {
    message += `<br /><a style="color: ${toastColor}" href="/${template.attributes.resource_type.toLowerCase()}">${
      template.attributes.action_text
    }</a>`;
  }

  return message;
};

const createToastConfig = (
  template: MessageType,
  newToastId: string,
  message: string,
  color: string,
  toast?: ToastProps
): NotificationProps => ({
  id: newToastId,
  title: template.attributes.title,
  message,
  className: `toast toast-${template.attributes.trigger}`,
  autoClose:
    toast?.autoClose ||
    (template.attributes.auto_close_seconds
      ? template.attributes.auto_close_seconds * 1000
      : false),
  styles: () => ({
    root: {
      alignItems: "flex-start",
    },
    icon: {
      backgroundColor: "transparent !important",
      ...(color && { color: `${color} !important` }),
    },
  }),
  ...(template.attributes.icon && {
    icon: getDynamicIcon(template.attributes.icon),
  }),
  ...toast,
});

const useToasts = (): {
  showToast: (toastReq: ToastsTriggerProps) => void;
  getMessageTemplates: () => MessageType[];
} => {
  const toasts = useNotifications();
  const { data: messageTemplates } = useMessageTemplates();

  const showToast = useCallback(
    (toastReq: ToastsTriggerProps): void => {
      const template = getToastTemplate(messageTemplates, toastReq);
      if (!template) return;

      const color = getToastColor(template, toastReq.toast?.color);
      const newToastId = `toast-${new Date().getTime()}`;
      const message = createToastMessage(template, toastReq.data, color);

      const tempQueue = [...toasts.queue];
      const tempToasts = [...toasts.notifications];
      toasts.cleanQueue();

      const newToast = createToastConfig(
        template,
        newToastId,
        message,
        color,
        toastReq.toast
      );
      const newToasts = [newToast, ...tempToasts, ...tempQueue];

      newToasts.forEach((t, index) => {
        if (index < tempToasts.length) {
          toasts.updateNotification(tempToasts[index].id as string, {
            ...t,
            id: tempToasts[index].id,
          });
        } else {
          toasts.showNotification({ ...t, id: undefined });
        }
      });
    },
    [toasts, messageTemplates]
  );

  const getMessageTemplates = useCallback(
    (): MessageType[] => messageTemplates?.data || [],
    [messageTemplates]
  );

  return { showToast, getMessageTemplates };
};

export { useToasts };
